import { Injectable } from "@angular/core";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { IDateRange } from "@app/interfaces/IDateRange";
import { TranslationService } from "./translation.service";

export interface IDateFormatOptions {
    longMonth?: boolean;
    excludeYear?: boolean;
    dateTime?: boolean;
    timeOnly?: boolean;
    second?: 'numeric' | '2-digit';
    stringDayOnly?: boolean;
}

export interface IDurationFormatOptions {
    style: "long" | "narrow"
}

export interface IDurationFormatDuration {
    years?: number,
    months?: number,
    weeks?: number,
    days?: number,
    hours?: number,
    minutes?: number,
    seconds?: number,
    milliseconds?: number
}

export enum DateUnit {
    Ms,
    Sec,
    Min,
    Hour,
    Day,
    Week,
    Month,
    Year
}
@Injectable({
    providedIn: 'root'
})
export class DateService {

    locale: string;

    constructor(private readonly translationService: TranslationService) {
        this.locale = this.getUserLocale();
    }

    formatDate(date: Date | string, options?: IDateFormatOptions): string {
        if (!date) {
            return this.translationService.t("Common.Never");
        }

        let parsedDate = new Date(date);

        let dateOptions: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
        let timeOptions: Intl.DateTimeFormatOptions = { hour12: false, hour: 'numeric', minute: 'numeric', second: options?.second };

        if (!options) {
            return parsedDate.toLocaleDateString(this.locale, dateOptions);
        }

        if (options.excludeYear) {
            dateOptions.year = undefined;
        }

        if (options.longMonth) {
            dateOptions.month = "long";
        }

        if (options.dateTime) {
            const dateString = parsedDate.toLocaleDateString(this.locale, dateOptions);
            let timeString = parsedDate.toLocaleTimeString(this.locale, timeOptions);
            timeString = this.setMidnightToZeroes(timeString);

            return `${timeString}, ${dateString}`;
        }

        if (options.timeOnly) {
            let timeString = parsedDate.toLocaleTimeString(this.locale, timeOptions);
            timeString = this.setMidnightToZeroes(timeString);

            return timeString;
        }

        if (options.stringDayOnly) {
            return this.getDayOfWeek(parsedDate.getDay());
        }


        return parsedDate.toLocaleDateString(this.locale, dateOptions);
    }

    formatTime(time: string): number {
        var value = parseInt(time);

        return isNaN(value) ? 0 : value;
    }

    getLocalTimezone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    getUTCOffsetLabel(): string {
        const now: Date = new Date();
        const offsetMinutes: number = now.getTimezoneOffset();
        const symbol: string = (offsetMinutes > 0) ? "-" : "+";
        const offsetHours: number = Math.abs(Math.floor(offsetMinutes / 60));

        return `UTC${symbol}${offsetHours}`;
    }

    getDurationFromTimeSpan(timespanValue: string): IDurationFormatDuration {
        var array = timespanValue.split(":");

        return {
            hours: this.formatTime(array[0]),
            minutes: this.formatTime(array[1]),
            seconds: this.formatTime(array[2])
        }
    }

    formatDatesFromAndTo(dateFrom: Date | string, dateTo: Date | string): string {
        if (this.isSameDate(dateFrom, dateTo)) {
            return this.translationService.t('VeriDateService.FromTime1ToTime2OnDate', [this.formatDate(dateFrom, { timeOnly: true }), this.formatDate(dateTo, { timeOnly: true }), this.formatDate(dateFrom)])
        }

        return this.translationService.t('VeriDateService.FromDate1ToDate2', [this.formatDate(dateFrom), this.formatDate(dateTo)]);
    }

    relativeTime(date: Date | string): string {

        date = new Date(date);

        const locale = this.getUserLocale();

        const diffMs = this.getDiff(new Date(), date, DateUnit.Ms);
        const timeAgoFormat = new Intl.RelativeTimeFormat(locale, { style: 'long' });

        if (isNaN(diffMs)) {
            return this.translationService.t("VeriDateService.Never");
        }

        if (Math.abs(this.msToDateUnit(diffMs, DateUnit.Min)) < 1) {
            return this.translationService.t("VeriDateService.JustNow");
        }
        if (Math.abs(this.msToDateUnit(diffMs, DateUnit.Year)) > 5) { // older than 5 years
            return this.translationService.t("VeriDateService.LongTimeAgo");
        }

        let diff = this.customRound(this.msToDateUnit(diffMs, DateUnit.Min));

        if (Math.abs(this.msToDateUnit(diffMs, DateUnit.Year)) > 1) {
            diff = this.customRound(this.msToDateUnit(diffMs, DateUnit.Year));
            return timeAgoFormat.format(diff, 'year')
        }

        if (Math.abs(this.msToDateUnit(diffMs, DateUnit.Day)) > 1) {
            diff = this.customRound(this.msToDateUnit(diffMs, DateUnit.Day));
            return timeAgoFormat.format(diff, 'day')
        }
        if (Math.abs(this.msToDateUnit(diffMs, DateUnit.Hour)) > 1) {
            diff = this.customRound(this.msToDateUnit(diffMs, DateUnit.Hour));
            return timeAgoFormat.format(diff, 'hour')
        }
        if (Math.abs(this.msToDateUnit(diffMs, DateUnit.Min)) > 1) {
            diff = this.customRound(this.msToDateUnit(diffMs, DateUnit.Min));
            return timeAgoFormat.format(diff, 'minute')
        }
    }

    convertToMilliseconds(unitOfTime: DateUnit, value: number): number {
        let milliseconds = 0;

        switch (unitOfTime) {
            case DateUnit.Week:
                milliseconds = value * 7 * 24 * 60 * 60 * 1000;
                break;
            case DateUnit.Day:
                milliseconds = value * 24 * 60 * 60 * 1000;
                break;
            case DateUnit.Hour:
                milliseconds = value * 60 * 60 * 1000;
                break;
            case DateUnit.Min:
                milliseconds = value * 60 * 1000;
                break;
            case DateUnit.Sec:
                milliseconds = value * 1000;
                break;
            case DateUnit.Ms:
                return value;
                break;
            default:
                throw new Error('Invalid date unit');
        }

        return milliseconds;
    }

    formatTimeFromMilliseconds(milliseconds: number, format: IDurationFormatOptions = { style: "long" }): string {
        if (!milliseconds && milliseconds != 0) {
            return;
        }

        const millisecondsInSecond = 1000;
        const millisecondsInMinute = 60 * millisecondsInSecond;
        const millisecondsInHour = 60 * millisecondsInMinute;
        const millisecondsInDay = 24 * millisecondsInHour;

        const days = Math.floor(milliseconds / millisecondsInDay);
        const remainingMilliseconds = milliseconds % millisecondsInDay;

        const hours = Math.floor(remainingMilliseconds / millisecondsInHour);
        const remainingMillisecondsAfterHours = remainingMilliseconds % millisecondsInHour;

        const minutes = Math.floor(remainingMillisecondsAfterHours / millisecondsInMinute);
        const remainingMillisecondsAfterMinutes = remainingMillisecondsAfterHours % millisecondsInMinute;

        const seconds = Math.floor(remainingMillisecondsAfterMinutes / millisecondsInSecond);

        var duration: IDurationFormatDuration = {
            days: days,
            hours: hours, minutes: minutes,
            seconds: seconds,
            milliseconds: milliseconds
        };

        // Pass zero years and months, as we have leap years + different days per month.
        return this.getDurationFormatting(duration, format);
    }

    formatDuration(date1: Date | string, date2: Date | string, format: IDurationFormatOptions = { style: "long" }): string {
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DurationFormat is an option but still in experimental stages as of July 2023
        if (this.date1IsAfterDate2(date1, date2)) {
            console.warn("Date 1 is after Date 2, unable to format duration");
            return;
        }

        date1 = new Date(date1);
        date2 = new Date(date2);

        const diffMs = this.getDiff(date1, date2, DateUnit.Ms);

        var duration: IDurationFormatDuration = {
            years: this.getYearsDifference(date1, date2),
            months: this.getMonthsDifference(date1, date2) % 12,
            days: this.getDaysDifference(date1, date2),
            hours: Math.abs(this.msToDateUnit(diffMs, DateUnit.Hour)) % 24,
            minutes: Math.abs(this.msToDateUnit(diffMs, DateUnit.Min)) % 60,
            seconds: Math.abs(this.msToDateUnit(diffMs, DateUnit.Sec)) % 60
        }

        return this.getDurationFormatting(duration, format);
    }

    formatDateRange(startDate: Date, endDate: Date): string {
        if (startDate.getDate() == endDate.getDate() && startDate.getMonth() == endDate.getMonth()) {

            return this.formatDate(startDate, { longMonth: true });
        }

        var label = this.formatDate(startDate, { excludeYear: true })
        label += " - ";
        label += this.formatDate(endDate);

        return label;
    }

    getDurationFormatting(duration: IDurationFormatDuration, format: IDurationFormatOptions = { style: "long" }): string {
        var durationString = "";
        var executionCount = 0;
        const maxExecutionCount = 1;

        var createSeparatorIfRequired = (durationString) => {
            if (executionCount > 1) {
                durationString = durationString + ", ";
            }

            return durationString;
        }

        if (Math.floor(duration.years) > 0 && ++executionCount <= maxExecutionCount) {
            durationString += format.style == "narrow"
                ? `${Math.floor(duration.years)}y`
                : `${this.translationService.t("VeriDateService.Years", Math.floor(duration.years))}`;
        }

        if (Math.floor(duration.months) > 0 && ++executionCount <= maxExecutionCount) {
            durationString = createSeparatorIfRequired(durationString);

            durationString += format.style == "narrow"
                ? `${Math.floor(duration.months)}m`
                : `${this.translationService.t("VeriDateService.Months", Math.floor(duration.months))}`;
        }

        if (Math.floor(duration.days) > 0 && ++executionCount <= maxExecutionCount) {
            durationString = createSeparatorIfRequired(durationString);

            durationString += format.style == "narrow"
                ? `${Math.floor(duration.days)}d`
                : `${this.translationService.t("VeriDateService.Days", Math.floor(duration.days))}`;
        }

        if (Math.floor(duration.hours) > 0 && ++executionCount <= maxExecutionCount) {
            durationString = createSeparatorIfRequired(durationString);

            durationString += format.style == "narrow"
                ? `${Math.floor(duration.hours)}h`
                : `${this.translationService.t("VeriDateService.Hours", Math.floor(duration.hours))}`;
        }

        if (Math.floor(duration.minutes) > 0 && ++executionCount <= maxExecutionCount) {
            durationString = createSeparatorIfRequired(durationString);

            durationString += format.style == "narrow"
                ? `${Math.floor(duration.minutes)}m`
                : `${this.translationService.t("VeriDateService.Minutes", Math.floor(duration.minutes))}`;
        }

        if (Math.floor(duration.seconds) > 0 && ++executionCount <= maxExecutionCount) {
            durationString = createSeparatorIfRequired(durationString);

            durationString += format.style == "narrow"
                ? `${Math.floor(duration.seconds)}s`
                : `${this.translationService.t("VeriDateService.Seconds", Math.floor(duration.seconds))}`;
        }

        if (duration.milliseconds < 1000 && duration.milliseconds > 0 && executionCount++ <= maxExecutionCount) {
            durationString = createSeparatorIfRequired(durationString);
            durationString += format.style == "narrow"
                ? `${Math.floor(duration.milliseconds)}ms`
                : `${this.translationService.t("VeriDateService.Milliseconds", duration.milliseconds)}`;
        }

        if (durationString === "") {
            durationString += format.style == "narrow"
                ? `${0}s`
                : `${this.translationService.t("VeriDateService.Seconds", 0)}`;
        }

        return durationString;
    }

    getYearsDifference(date1: Date | string, date2: Date | string): number {
        date1 = new Date(date1);
        date2 = new Date(date2);

        let years = date2.getFullYear() - date1.getFullYear();

        if (years > 0 && (date2.getMonth() < date1.getMonth() || date2.getMonth() == date1.getMonth() && date1.getDate() - date2.getDate() > 0)) {
            years--;
        }

        return years;
    }

    getMonthsDifference(date1: Date | string, date2: Date | string): number {
        date1 = new Date(date1);
        date2 = new Date(date2);

        let months: number = date2.getMonth() - date1.getMonth();

        if (months >= 0 && date1.getDate() - date2.getDate() <= 0) {
            return months;
        }

        if (months > 0 && date1.getDate() - date2.getDate() > 0) {
            months--;
        }
        else {
            months = date2.getMonth() + (11 - date1.getMonth());
        }

        return months;
    }

    getDaysDifference(date1: Date | string, date2: Date | string): number {
        date1 = new Date(date1);
        date2 = new Date(date2);

        const timeDifference: number = date2.getTime() - date1.getTime();
        const daysDifference: number = timeDifference / (1000 * 60 * 60 * 24);

        if (daysDifference >= 0) {
            return daysDifference;
        }

        const daysInMonth = (year, month) => new Date(year, month, 0).getDate();
        const numberOfDaysInPreviousMonth = daysInMonth(date2.getFullYear(), date2.getMonth());

        const days = date2.getDate() + (numberOfDaysInPreviousMonth - date1.getDate());

        return days;
    }

    getDiff(date1: Date | string, date2: Date | string, unit: DateUnit): number {

        date1 = new Date(date1);
        date2 = new Date(date2);

        var ms = date2.getTime() - date1.getTime();

        // Add one millisecond to account for end-of-day 23:59:59
        return this.msToDateUnit(ms + 1, unit)
    }

    addOrSubtractTime(date: Date | string, amount: number, unit: DateUnit): Date {
        if (!this.allDatesAreValid([new Date(date)])) {
            return;
        }

        const dateMs = new Date(date).getTime();

        switch (unit) {
            case DateUnit.Ms:
                return new Date(dateMs + amount);

            case DateUnit.Sec:
                return new Date(dateMs + (amount * 1000));

            case DateUnit.Min:
                return new Date(dateMs + (amount * 1000 * 60));

            case DateUnit.Hour:
                return new Date(dateMs + (amount * 1000 * 60 * 60));

            case DateUnit.Day:
                let dD = new Date(date);
                dD.setDate(dD.getDate() + amount);
                return dD;

            case DateUnit.Week:
                return new Date(dateMs + (amount * 1000 * 60 * 60 * 24 * 7));

            case DateUnit.Month:
                let mD = new Date(date);
                mD.setMonth(mD.getMonth() + amount);
                return mD;

            default:
                return null;
        }
    }

    dateIsBetweenDates(date: Date | string, start: Date | string, end: Date | string, includeStart?: boolean, includeEnd?: boolean) {
        const parsedDate = new Date(date);
        const parsedStart = new Date(start);
        const parsedEnd = new Date(end);

        if (!this.allDatesAreValid([parsedDate, parsedStart, parsedEnd])) {
            return;
        }

        let dateIsAfterStart = false;
        let dateIsBeforeEnd = false;

        if (includeStart) {
            dateIsAfterStart = parsedDate.getTime() >= parsedStart.getTime()
        }
        else {
            dateIsAfterStart = parsedDate.getTime() > parsedStart.getTime()
        }


        if (includeEnd) {
            dateIsBeforeEnd = parsedDate.getTime() <= parsedEnd.getTime();
        }
        else {
            dateIsBeforeEnd = parsedDate.getTime() < parsedEnd.getTime();
        }

        return dateIsAfterStart && dateIsBeforeEnd;
    }

    getStartOfDay(date: Date | string) {
        const parsedDate = new Date(date);

        if (!this.allDatesAreValid([parsedDate])) {
            return;
        }

        parsedDate.setHours(0, 0, 0, 0);
        return parsedDate;
    }

    getEndOfDay(date: Date | string): Date {
        const parsedDate = new Date(date);

        if (!this.allDatesAreValid([parsedDate])) {
            return;
        }

        parsedDate.setHours(23, 59, 59);
        return parsedDate;
    }

    getStartOfWeek(date: Date | string): Date {
        const parsedDate = new Date(date);

        if (!this.allDatesAreValid([parsedDate])) {
            return;
        }

        const dayOfWeek = parsedDate.getDay();

        const startOfWeekDate = this.addOrSubtractTime(parsedDate, -dayOfWeek + 1, DateUnit.Day);

        return new Date(this.getStartOfDay(startOfWeekDate));

    }

    getEndOfWeek(date: Date | string): Date {
        const parsedDate = new Date(date);

        if (!this.allDatesAreValid([parsedDate])) {
            return;
        }

        const daysToAdd = 7 - parsedDate.getDay();

        return new Date(this.addOrSubtractTime(parsedDate, daysToAdd, DateUnit.Day));
    }

    getStartOfMonth(date: Date | string): Date {
        const parsedDate = new Date(date);

        if (!this.allDatesAreValid([parsedDate])) {
            return;
        }

        parsedDate.setDate(1);

        return new Date(this.getStartOfDay(parsedDate));

    }

    getEndOfMonth(date: Date | string): Date {
        const parsedDate = new Date(date);

        if (!this.allDatesAreValid([parsedDate])) {
            return;
        }

        const endOfMonth = new Date(parsedDate.getFullYear(), parsedDate.getMonth() + 1, 0);

        return new Date(this.getEndOfDay(endOfMonth));
    }

    isSameDate(date1: Date | string, date2: Date | string, includeTime: boolean = false): boolean {

        if (!date1 || !date2) {
            return false;
        }

        const parsedDate1 = new Date(date1);
        const parsedDate2 = new Date(date2);

        if (!this.allDatesAreValid([parsedDate1, parsedDate2])) {
            return false;
        }

        const sameYear = parsedDate1.getFullYear() === parsedDate2.getFullYear();
        const sameMonth = parsedDate1.getMonth() === parsedDate2.getMonth();
        const sameDay = parsedDate1.getDate() === parsedDate2.getDate();

        if (includeTime) {
            const sameTime = Math.floor((parsedDate1.getTime() / 1000)) === Math.floor((parsedDate2.getTime() / 1000));

            return sameYear && sameMonth && sameDay && sameTime;
        }

        return sameYear && sameMonth && sameDay;
    }

    isSameMonth(date1: Date | string, date2: Date | string): boolean {

        const parsedDate1 = new Date(date1);
        const parsedDate2 = new Date(date2);

        if (!this.allDatesAreValid([parsedDate1, parsedDate2])) {
            return false;
        }

        const sameYear = parsedDate1.getFullYear() === parsedDate2.getFullYear();
        const sameMonth = parsedDate1.getMonth() === parsedDate2.getMonth();

        return sameYear && sameMonth;
    }

    date1IsAfterDate2(date1: Date | string, date2: Date | string, includeIsSame?: boolean): boolean {
        const parsedDate1 = new Date(date1);
        const parsedDate2 = new Date(date2);

        if (!this.allDatesAreValid([parsedDate1, parsedDate2])) {
            return false;
        }

        if (includeIsSame) {
            return parsedDate1.getTime() >= parsedDate2.getTime();
        }

        return parsedDate1.getTime() > parsedDate2.getTime();
    }

    isAfter(beforeDate: Date | string, afterDate: Date | string): boolean {
        return this.date1IsAfterDate2(beforeDate, afterDate)
    }

    allDatesAreValid(dates: Date[]): boolean {
        return !dates.some(date => isNaN(date.getTime()));
    }

    getLatestDate(dates: Date[]): Date {

        var parsedDates: Date[] = [];

        dates.forEach(d => parsedDates.push(new Date(d)));

        if (!this.allDatesAreValid(parsedDates)) {
            return null;
        }

        var latestDate = null

        parsedDates.forEach(d => {
            if (latestDate == null) {
                latestDate = d;
                return;
            }

            if (this.date1IsAfterDate2(d, latestDate)) {
                latestDate = d;
            }
        });

        return latestDate;
    }

    getDateWithSuffix(date: string | number): string {
        let dateAsNumber = Math.trunc(Number(date));
        if (dateAsNumber < 0 || dateAsNumber > 31) {
            console.error(date, "is not a valid date");
            return;
        }

        return this.getOrdinalSuffix(dateAsNumber);
    }

    getOrdinalSuffix(number: number) {

        const locale = localStorage.getItem('userSelectedLanguageCode');

        if (locale !== 'en') {
            // No current support for translation
            return String(number);
        }

        let numberAsString = String(number);
        const smallestUnit = numberAsString.split('').pop();
        switch (Number(smallestUnit)) {
            case 1:
                return this.translationService.t("VeriDateService.stSuffix", numberAsString);
            case 2:
                return this.translationService.t("VeriDateService.ndSuffix", numberAsString);
            case 3:
                return this.translationService.t("VeriDateService.rdSuffix", numberAsString);
            default:
                return this.translationService.t("VeriDateService.thSuffix", numberAsString);
        }
    }

    updateTime(date: Date, timeString: string): Date {
        if (!date) {
            return;
        }

        const hoursMinutesSeconds = timeString.split(':');
        const hours = hoursMinutesSeconds[0];
        const minutes = hoursMinutesSeconds[1];
        const seconds = hoursMinutesSeconds[2] || '00';

        let updatedHoursDate = new Date(date).setHours(Number(hours));
        let updatedMinutesDate = new Date(updatedHoursDate).setMinutes(Number(minutes));
        let updatedSecondsDate = new Date(updatedMinutesDate).setSeconds(Number(seconds));

        return new Date(updatedSecondsDate);
    }


    getTimeAsString(date: Date | string, includeSeconds: boolean = false): string {
        const parsedDate = new Date(date);
        const hours = this.getHoursAsString(parsedDate);

        const minutes = this.getMinutesAsString(parsedDate);

        const seconds = this.getSecondsAsString(parsedDate);

        if (includeSeconds) {
            return `${hours}:${minutes}:${seconds}`;
        }

        return `${hours}:${minutes}`;
    }

    sort(date1: string | Date, date2: string | Date, sortDirection: SortDirection = SortDirection.Descending): number {
        var parsedDate1 = new Date(date1);
        var parsedDate2 = new Date(date2);

        const parsedDate1Time = parsedDate1.getTime();
        if (isNaN(parsedDate1Time)) {
            console.error(date1, "is not a date", typeof date1);
        }

        if (sortDirection == SortDirection.Ascending) {
            return parsedDate1Time - parsedDate2.getTime();
        }
        else {
            return parsedDate2.getTime() - parsedDate1Time;
        }
    }

    getMinuteOfDay(date: Date): number {

        const parsedDate = new Date(date);

        const hours = parsedDate.getHours();
        const minutes = parsedDate.getMinutes();

        return (hours * 60) + minutes;
    }

    getSecondOfDay(date: Date): number {

        const parsedDate = new Date(date);

        const hours = parsedDate.getHours();
        const minutes = parsedDate.getMinutes();
        const seconds = parsedDate.getSeconds();

        return (hours * 3600) + (minutes * 60) + seconds;
    }

    dateRangeFromEnum(dateRange: DateRange): IDateRange {
        let startDate: Date = this.getStartOfDay(new Date());
        let endDate: Date = this.getEndOfDay(new Date());

        let rangeEndsYesterday = false;

        switch (dateRange) {
            case DateRange.Today:
                startDate = this.getStartOfDay(new Date());
                break;

            case DateRange.Yesterday:
                startDate = this.addOrSubtractTime(new Date(), -1, DateUnit.Day);
                endDate = new Date(this.getEndOfDay(startDate));
                break;

            case DateRange.Week:
                startDate = this.addOrSubtractTime(new Date(), -1, DateUnit.Week);
                rangeEndsYesterday = true;
                break;

            case DateRange.Last7Days:
                startDate = this.addOrSubtractTime(new Date(), -6, DateUnit.Day);
                rangeEndsYesterday = true;
                break;

            case DateRange.Last14Days:
                startDate = this.addOrSubtractTime(new Date(), -14, DateUnit.Day);
                rangeEndsYesterday = true;
                break;

            case DateRange.Last30Days:
                startDate = this.addOrSubtractTime(new Date(), -30, DateUnit.Day);
                rangeEndsYesterday = true;
                break;

            case DateRange.ThisWeek:
                startDate = this.getStartOfWeek(new Date());
                endDate = new Date(this.addOrSubtractTime(startDate, 6, DateUnit.Day));
                break;

            case DateRange.ThisMonth:
                startDate = this.getStartOfMonth(new Date());
                endDate = this.getEndOfMonth(new Date());
                break;

            case DateRange.Latest3Weeks:
                startDate = this.addOrSubtractTime(this.getStartOfWeek(new Date()), -2, DateUnit.Week);
                break;

            case DateRange.Latest14Days:
                startDate = this.addOrSubtractTime(new Date(), -14, DateUnit.Day);
                break;

            case DateRange.Latest4Weeks:
                startDate = this.addOrSubtractTime(new Date(), -28, DateUnit.Day);
                break;

            case DateRange.Latest7Days:
                startDate = this.addOrSubtractTime(new Date(), -7, DateUnit.Day);
                break;

            case DateRange.LastWeek:
                startDate = this.addOrSubtractTime(this.getStartOfWeek(new Date()), -1, DateUnit.Week);
                endDate = new Date(this.getEndOfWeek(startDate));
                break;

            case DateRange.LastMonth:
                startDate = this.getStartOfMonth(this.addOrSubtractTime(new Date(), -1, DateUnit.Month));
                endDate = new Date(this.getEndOfMonth(startDate));
                break;

            case DateRange.Latest4Weeks:
                startDate = this.addOrSubtractTime(new Date(), 4, DateUnit.Week);
                break;
        }

        if (rangeEndsYesterday) {
            startDate = new Date(this.addOrSubtractTime(startDate, -1, DateUnit.Day));
            endDate = new Date(this.addOrSubtractTime(endDate, -1, DateUnit.Day));
        }

        return {
            start: startDate,
            end: endDate
        }
    }

    formatTimeDuration(seconds: number): string {
        if (!seconds) {
            return "00:00";
        }

        const mm = Math.floor(seconds / 60);
        const ss = Math.floor(seconds % 60);

        return `${mm < 10 ? "0" : ""}${mm}:${ss < 10 ? "0" : ""}${ss}`;
    }

    getDateTimeFromSecondOfDay(secondOfDay: number, date: Date): Date {
        const hours = Math.floor(secondOfDay / 3600);
        const minutes = Math.floor((secondOfDay % 3600) / 60);
        const seconds = Math.floor(secondOfDay % 60);

        return this.updateTime(date, `${hours}:${minutes}:${seconds}`)
    }

    private customRound(value: number): number {
        return value > 0 ? Math.floor(value) : Math.ceil(value);
    }

    getDayOfWeek(day: number, shorten = false): string {
        var translationKey = "";

        switch (day) {
            case 0:
                translationKey = "VeriDateService.Sunday";
                break;

            case 1:
                translationKey = "VeriDateService.Monday";
                break;

            case 2:
                translationKey = "VeriDateService.Tuesday";
                break;

            case 3:
                translationKey = "VeriDateService.Wednesday";
                break;

            case 4:
                translationKey = "VeriDateService.Thursday";
                break;

            case 5:
                translationKey = "VeriDateService.Friday";
                break;

            case 6:
                translationKey = "VeriDateService.Saturday";
                break;
        }

        if (shorten) {
            return this.translationService.t(translationKey).substr(0, 3);
        }

        return this.translationService.t(translationKey);
    }

    parseTimeSpan(timeSpan: string): number {
        if (!timeSpan) return 0;
        if (typeof timeSpan === "number") return Math.floor(timeSpan);
        
        let totalSeconds = 0;
        let days = 0, hours = 0, minutes = 0, seconds = 0;
        
        // Handle days if present
        if (timeSpan.includes(".")) {
            let [dayPart, timePart] = timeSpan.split(".");
            days = parseInt(dayPart, 10);
            timeSpan = timePart;
        }
        
        let timeSplit = timeSpan.split(":").map(Number);
        if (timeSplit.length === 3) {
            [hours, minutes, seconds] = timeSplit;
        }
        
        totalSeconds = (days * 24 * 3600) + (hours * 3600) + (minutes * 60) + seconds;
        return totalSeconds;
    }
    
    // ---

    private getHoursAsString(date: Date): string {
        let hours = date.getHours().toString();
        if (hours.length < 2) {
            hours = "0" + hours;
        }

        return hours;
    }

    private getMinutesAsString(date: Date): string {
        let minutes = date.getMinutes().toString();
        if (minutes.length < 2) {
            minutes = "0" + minutes;
        }

        return minutes;
    }

    private getSecondsAsString(date: Date): string {
        let seconds = date.getSeconds().toString();
        if (seconds.length < 2) {
            seconds = "0" + seconds;
        }

        return seconds;
    }

    private msToDateUnit(ms: number, unit: DateUnit): number {
        switch (unit) {
            case DateUnit.Ms:
                return ms;

            case DateUnit.Sec:
                return ms / 1000;

            case DateUnit.Min:
                return ms / 1000 / 60;

            case DateUnit.Hour:
                return ms / 1000 / 60 / 60;

            case DateUnit.Day:
                return ms / 1000 / 60 / 60 / 24;

            case DateUnit.Year:
                return ms / 1000 / 60 / 60 / 24 / 365;
        }
    }

    private getUserLocale(): string {
        let locale = localStorage.getItem('userSelectedLanguageCode');

        if (!locale || locale === 'en') {
            // So we can differentiate between en-GB and en-US
            locale = this.getLocaleFromBrowser();
        }

        return locale;
    }

    private getLocaleFromBrowser() {
        if (navigator.languages != undefined) {
            return navigator.languages[0];
        }

        return navigator.language;
    }

    private setMidnightToZeroes(timeString: string): string {
        // Ensure midnight always shown as "00" and never "24" which sometimes happens when Chrome locale is "en-US" 
        if (timeString.substring(0, 2) == "24") {
            timeString = timeString.replace(/^.{2}/, "00");
        }

        return timeString;
    }
}
